import supabase from './supabaseClient';

// ── Page-name mapping (path → friendly label) ──────────────
const PAGE_NAMES = {
  '/main-dashboard': 'Dashboard',
  '/food-calendar-view': 'Food Calendar',
  '/delivery-status': 'Delivery Status',
  '/meals-management': 'Meals Management',
  '/meal-detail-view': 'Meal Detail',
  '/ingredients-management': 'Ingredients',
  '/cost-analytics': 'Cost Analytics',
  '/cost-analytics/breakdown': 'Cost Breakdown',
  '/compliance-reporting': 'Compliance',
  '/audit-logs': 'Audit Logs',
  '/community': 'Community',
  '/help': 'Help',
  '/admin/dashboard': 'Admin Dashboard',
  '/admin/user-management': 'User Management',
  '/admin/care-home-management': 'Care Home Management',
  '/admin/resident-management': 'Resident Management',
  '/super-admin': 'Admin Portal',
  '/super-admin/subscriptions': 'Admin Portal (legacy redirect)',
  '/super-admin/meal-approvals': 'Meal Approvals',
  '/super-admin/user-activity': 'User Activity Tracker',
};

export const getPageName = (path) => PAGE_NAMES[path] || path;

// ── Cached auth token for beacon (sync access during unload) ──
let _cachedAccessToken = '';
const refreshCachedToken = async () => {
  if (!supabase) return;
  try {
    const { data } = await supabase.auth.getSession();
    _cachedAccessToken = data?.session?.access_token || '';
  } catch { /* best-effort */ }
};

// ── Log a page entry (fire-and-forget safe) ─────────────────
export const logPageEntry = async (userId, sessionId, pagePath, careHomeId = null) => {
  if (!supabase || !userId) return null;
  const pageName = getPageName(pagePath);
  try {
    const { data, error } = await supabase
      .from('user_activity_logs')
      .insert([{
        user_id: userId,
        session_id: sessionId,
        page_path: pagePath,
        page_name: pageName,
        care_home_id: careHomeId || null,
        source_app: 'meal_manager',
        entered_at: new Date().toISOString(),
      }])
      .select('id')
      .single();

    if (error) {
      console.error('Activity log insert error:', error.message);
      return null;
    }
    // Refresh cached token after successful write (keeps it fresh)
    refreshCachedToken();
    return data?.id;
  } catch (err) {
    console.error('Activity log insert exception:', err);
    return null;
  }
};

// ── Mark a page as left (uses RPC for atomic duration calc) ──
export const logPageExit = async (logId) => {
  if (!supabase || !logId) return;
  const leftAt = new Date().toISOString();
  try {
    const { error } = await supabase.rpc('close_activity_log', {
      log_id: logId,
      left_at_ts: leftAt,
    });
    if (error) {
      // Fallback: direct update (trigger will compute duration)
      await supabase
        .from('user_activity_logs')
        .update({ left_at: leftAt })
        .eq('id', logId);
    }
  } catch {
    // Best-effort — don't block navigation
  }
};

// ── Beacon-based exit (for beforeunload — sync, keepalive) ──
export const logPageExitBeacon = (logId) => {
  if (!logId) return;
  const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/user_activity_logs?id=eq.${logId}`;
  const leftAt = new Date().toISOString();
  try {
    fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${_cachedAccessToken}`,
        'Prefer': 'return=minimal',
      },
      body: JSON.stringify({ left_at: leftAt }),
      keepalive: true,
    });
  } catch {
    // Best-effort — nothing more we can do during unload
  }
};

// ── Fetch all activity logs with user info (Super Admin) ────
export const getActivityLogs = async ({ userId, startDate, endDate, page = 0, pageSize = 50 } = {}) => {
  if (!supabase) return { data: [], count: 0 };

  let query = supabase
    .from('user_activity_logs')
    .select('*', { count: 'exact' })
    .eq('source_app', 'meal_manager')
    .order('entered_at', { ascending: false })
    .range(page * pageSize, (page + 1) * pageSize - 1);

  if (userId) query = query.eq('user_id', userId);
  if (startDate) query = query.gte('entered_at', startDate);
  if (endDate) query = query.lte('entered_at', endDate);

  const { data, error, count } = await query;
  if (error) {
    console.error('Fetch activity logs error:', error.message);
    return { data: [], count: 0 };
  }
  return { data: data || [], count };
};

// ── Get per-user summary stats (uses server-side RPC when available) ──
export const getUserActivitySummary = async ({ startDate, endDate, careHomeId } = {}) => {
  if (!supabase) return [];

  // Try server-side aggregation first (much faster)
  try {
    const { data, error } = await supabase.rpc('get_user_activity_summary', {
      start_date: startDate || null,
      end_date: endDate || null,
      p_care_home_id: careHomeId || null,
      p_source_app: 'meal_manager',
    });
    if (!error && data) {
      return data.map(row => ({
        user_id: row.user_id,
        total_seconds: Number(row.total_seconds) || 0,
        page_visits: Number(row.page_visits) || 0,
        last_active: row.last_active,
        pages: row.top_pages || {},
      }));
    }
  } catch { /* fall through to client-side */ }

  // Fallback: client-side aggregation (for before RPC is deployed)
  // Fallback: client-side aggregation (scoped to this app only)
  let query = supabase
    .from('user_activity_logs')
    .select('user_id, page_path, page_name, entered_at, left_at, duration_seconds')
    .eq('source_app', 'meal_manager');

  if (startDate) query = query.gte('entered_at', startDate);
  if (endDate) query = query.lte('entered_at', endDate);

  const { data, error } = await query.order('entered_at', { ascending: false }).limit(5000);
  if (error) {
    console.error('Activity summary error:', error.message);
    return [];
  }

  // Aggregate by user
  const byUser = {};
  for (const row of (data || [])) {
    if (!byUser[row.user_id]) {
      byUser[row.user_id] = { user_id: row.user_id, total_seconds: 0, page_visits: 0, pages: {}, last_active: null };
    }
    const u = byUser[row.user_id];
    u.page_visits++;

    let dur = row.duration_seconds;
    if (!dur && row.left_at && row.entered_at) {
      dur = Math.round((new Date(row.left_at) - new Date(row.entered_at)) / 1000);
    }
    if (dur && dur > 0) u.total_seconds += dur;

    const pName = row.page_name || row.page_path;
    u.pages[pName] = (u.pages[pName] || 0) + 1;

    if (!u.last_active || new Date(row.entered_at) > new Date(u.last_active)) {
      u.last_active = row.entered_at;
    }
  }

  return Object.values(byUser);
};

// ── Fetch user profiles for display ─────────────────────────
export const getActivityUsers = async (userIds) => {
  if (!supabase || !userIds?.length) return {};
  const { data, error } = await supabase
    .from('user_profiles')
    .select('id, name, email, role, care_home_id')
    .in('id', userIds);
  if (error) return {};
  const map = {};
  for (const p of (data || [])) map[p.id] = p;
  return map;
};
